<template>
  <div>
    <u-help-text
      class="float-right mb-2"
      help-id="inputCriteriaHelp"
      help-title="Information"
    >
      <p>
        Beim Doppelklick auf eine Eigenschaft in der Tabelle werden alle Items
        mit diesem gleichen Wert ausgewählt.
      </p>
      <p class="mt-2">
        Eine Regelkonfiguration muss mindestens ein Strukturelement bzw. eine
        Eingabegruppierung beinhalten, sonst ist sie ungültig. Dies verursacht
        eine fehlerhafte Mindestlohnkonfiguration.
      </p>
    </u-help-text>
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />
    <u-loader v-if="isLoading" />
    <b-tabs
      v-else-if="topGroups"
      :key="topGroups.length"
      v-model="activeTab"
      class="topGroupsTabControl mr-4"
    >
      <b-tab
        v-for="(topGroup, index) in topGroups"
        :key="index"
        :title="topGroup.name"
        @click="switchTab(index)"
      >
        <div v-if="activeTab == index">
          <b-card class="shadow-sm">
            <div class="float-right mr-4">
              <button
                v-if="canUserEdit"
                v-b-modal.confirmDeleteRulesGroup
                v-b-tooltip.hover
                class="btn btn-danger"
                placement="bottom"
                title="Regelgruppe löschen"
                type="button"
                @click="rulesGroupToDelete = topGroup"
              >
                <i class="fas fa-trash-alt" />
              </button>
            </div>
            <div class="float-right mr-1">
              <rule-configuration-edit
                :contract-id="contractId"
                :edition-id="editionId"
                :rulesgroup-configuration="JSON.parse(JSON.stringify(topGroup))"
                :version-id="versionId"
                @updated="getAllRuleGroups()"
              />
            </div>
            <h4 class="mb-4">
              {{ topGroup.name }}
            </h4>
            <rule-configuration
              :configuration="topGroup"
              :contract-id="contractId"
              :edition-id="editionId"
              :rules="topGroup.rules"
              :version-id="versionId"
              :expanded-rule-groups="expandedRuleGroups"
              :pagination="ruleGroupPagination"
              class="mt-4 w-100"
              @updated="getAllRuleGroups()"
              @toggleExpandedRuleGroups="handleRuleGroupToggleExpanded"
              @togglePagination="handleRuleGroupTogglePagination"
            />
          </b-card>
        </div>
      </b-tab>
      <b-tab style="padding: 0px">
        <template slot="title">
          <div
            v-if="canUserEdit"
            v-b-modal.confirmCreateNewRulesConfiguration
            v-b-tooltip.hover
            class="addRuleGroupButton"
            placement="bottom"
            title="Neue Regelkonfiguration / Regeln aus Vorlage importieren"
          >
            <i class="fas fa-plus" />
          </div>
        </template>
      </b-tab>
    </b-tabs>
    <b-modal
      id="confirmCreateNewRulesConfiguration"
      ref="confirmCreateNewRulesConfiguration"
      hide-footer
      no-fade
      title="Neue Regelgruppe erstellen"
    >
      <u-loader v-if="isImportLoading" />
      <div v-else>
        <template v-if="createErrorObject">
          <u-error-handling :error-object="createErrorObject" />
          <div
            id="my-collapse"
            class="mt-4"
            style="border: 1px solid lightgray; padding: 5px"
          >
            <b>Folgende Bedingungen müssen erfüllt sein, dass der Import
              funktioniert</b>
            <ul>
              <li>
                Es muss ein Stukturelement für Jahr (Titel = Jahr/Année/Anno)
                vorhanden sein
              </li>
              <li>
                Das Eingabekriterium für Jahr sollte mindestens das Jahr
                enthalten, das importiert werden soll
              </li>
              <li>
                Es muss ein Stukturelement für Kanton (Titel =
                Kanton/Canton/Cantone) vorhanden sein
              </li>
              <li>
                Es muss ein Stukturelement für Bezirk (Titel =
                Feiertagsbezirk/Bezirk/District/Distretto) vorhanden sein
              </li>
            </ul>
          </div>
        </template>
        <div
          v-else
          class="d-block mb-4"
        >
          Was möchten sie machen?
          <v-radio-group
            v-model="createConfigType"
            :mandatory="true"
          >
            <v-radio
              label="Neue Konfiguration erstellen"
              value="new"
            />
            <v-radio
              label="Aus Vorlage importieren"
              value="template"
            />
          </v-radio-group>
          <template v-if="createConfigType == 'template'">
            <button
              v-b-toggle.my-collapse
              v-b-tooltip.hover
              class="btn InformationButton float-right m-4"
              placement="bottom"
              title="Info"
              type="button"
            >
              <i class="fas fa-info-circle" />
            </button>

            <b-collapse
              id="my-collapse"
              class="mt-4"
              style="border: 1px solid lightgray; padding: 5px"
            >
              <b>Folgende Bedingungen müssen erfüllt sein, dass der Import
                funktioniert</b>
              <ul>
                <li>
                  Es muss ein Stukturelement für Jahr (Titel = Jahr/Année/Anno)
                  vorhanden sein
                </li>
                <li>
                  Das Eingabekriterium für Jahr sollte mindestens das Jahr
                  enthalten, das importiert werden soll
                </li>
                <li>
                  Es muss ein Stukturelement für Kanton (Titel =
                  Kanton/Canton/Cantone) vorhanden sein
                </li>
                <li>
                  Es muss ein Stukturelement für Bezirk (Titel =
                  Feiertagsbezirk/Bezirk/District/Distretto) vorhanden sein
                </li>
              </ul>
            </b-collapse>
            <v-select
              v-model="selectedTemplateId"
              :items="templates"
              item-text="name"
              item-value="id"
            />
          </template>
        </div>
        <b-button
          variant="success"
          @click="createNewRulesConfiguration()"
        >
          Erstellen
        </b-button>
        <b-button
          class="ml-2"
          variant="danger"
          @click="
            hideModal('confirmCreateNewRulesConfiguration');
            createErrorObject = null;
          "
        >
          Abbrechen
        </b-button>
      </div>
    </b-modal>
    <b-modal
      id="confirmDeleteRulesGroup"
      ref="confirmDeleteRulesGroup"
      hide-footer
      no-fade
      title="Regelgruppe löschen"
    >
      <div class="d-block mb-4">
        Sind Sie sicher, dass Sie diese Regelgruppe löschen möchten?
      </div>
      <b-button
        variant="success"
        @click="
          hideModal('confirmDeleteRulesGroup');
          deleteRulesGroup();
        "
      >
        Ok
      </b-button>
      <b-button
        class="ml-2"
        variant="danger"
        @click="hideModal('confirmDeleteRulesGroup')"
      >
        Abbrechen
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { MinimumWageMixin } from "@/components/minimum.wage/mixins/MinimumWageMixin";
import {
  GetRuleGroups,
  DeleteRuleGroup,
  CreateNewRulesConfiguration,
} from "@/services/minimum.wage.services/rules.minimum.wages.js";
import { CreateFromTemplate } from "@/services/minimum.wage.services/template.minimum.wages.js";
import RuleConfiguration from "@/components/minimum.wage/pages/Rules/RuleConfiguration.vue";
import RuleConfigurationEdit from "@/components/minimum.wage/pages/Rules/RuleConfigurationEdit.vue";

export default {
  components: {
    RuleConfigurationEdit,
    RuleConfiguration,
  },
  mixins: [MinimumWageMixin],
  data() {
    return {
      isLoading: false,
      isImportLoading: false,
      errorObject: null,
      createErrorObject: null,
      topGroups: [],
      expandedRuleGroups: [],
      ruleGroupPagination: {},
      rulesGroupToDelete: null,
      editGroup: null,
      activeTab: -1,
      createConfigType: "new",
      selectedTemplateId: null,
    };
  },
  computed: {
    templates() {
      return this.$store.getters.getTemplatesByType(0);
    },
  },
  created() {
    let that = this;
    this.getAllRuleGroups();

    this.$eventBus.$on("rulesChanged", () => {
      that.getAllRuleGroups();
    });
  },
  methods: {
    hideModal(modalId) {
      this.$refs[modalId].hide();
    },
    handleRuleGroupTogglePagination(event) {
      this.ruleGroupPagination = {
        ...this.ruleGroupPagination,
        [event.ruleGroupId]: event.pagination,
      };
    },
    handleRuleGroupToggleExpanded(groupId) {
      if (this.expandedRuleGroups.includes(groupId)) {
        this.expandedRuleGroups = this.expandedRuleGroups.filter(
          (x) => x !== groupId,
        );
      } else {
        this.expandedRuleGroups.push(groupId);
      }
    },
    switchTab(index) {
      this.$store.state.history.mindestLohnKonfiguration.ruleConfiguration.groupedRulesConfig.getChangesHistory().length = 0;
      this.$store.state.history.mindestLohnKonfiguration.ruleConfiguration.ungroupedRuleConfig.getChangesHistory().length = 0;
      this.activeTab = index;
    },
    getAllRuleGroups() {
      this.isLoading = true;
      this.errorObject = null;

      GetRuleGroups(
        this.contractId,
        this.versionId,
        this.editionId,
        (success) => {
          this.topGroups = success;
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
        },
      );
    },
    createNewRulesConfiguration() {
      this.isImportLoading = true;
      let that = this;
      this.createErrorObject = null;

      this.$store.state.history.mindestLohnKonfiguration.ruleConfiguration.groupedRulesConfig.getChangesHistory().length = 0;
      this.$store.state.history.mindestLohnKonfiguration.ruleConfiguration.ungroupedRuleConfig.getChangesHistory().length = 0;

      if (this.createConfigType === "new") {
        var newRulesConfigurationObject = {
          id: 0,
          configurationId: 0,
          name: "Neue Regel-Konfiguration",
          global: false,
          configEntries: [],
          ruleGroups: [],
          rules: [],
        };

        CreateNewRulesConfiguration(
          this.contractId,
          this.versionId,
          this.editionId,
          newRulesConfigurationObject,
          () => {
            that.getAllRuleGroups();
            that.hideModal("confirmCreateNewRulesConfiguration");
            this.isImportLoading = false;
          },
          (error) => {
            that.createErrorObject = error;
            this.isImportLoading = false;
          },
        );
      } else {
        // create rules from template
        CreateFromTemplate(
          this.selectedTemplateId,
          this.contractId,
          this.versionId,
          this.editionId,
          null, // no ruleconfigid set yet
          null, // no ruleGroupId sets
          () => {
            that.hideModal("confirmCreateNewRulesConfiguration");
            that.getAllRuleGroups();
            this.isImportLoading = false;
          },
          (error) => {
            that.createErrorObject = error;
            this.isImportLoading = false;
          },
        );
      }
    },
    deleteRulesGroup() {
      var ruleGroupToDelete = this.topGroups.filter((topGroup) => {
        return topGroup.id == this.rulesGroupToDelete.id;
      });

      if (ruleGroupToDelete && ruleGroupToDelete[0]) {
        this.isLoading = true;
        this.errorObject = null;

        DeleteRuleGroup(
          this.contractId,
          this.versionId,
          this.editionId,
          ruleGroupToDelete[0].id,
          () => {
            this.getAllRuleGroups();
          },
          (error) => {
            this.errorObject = error;
          },
        );
      }
    },
  },
};
</script>

<style>
.topGroupsTabControl .shadow-sm {
  border-top: none;
  border-radius: 0px;
}

.topGroupsTabControl li:last-child .nav-link {
  padding: 0px;
}
</style>

<style scoped>
.addRuleGroupButton {
  position: relative;
  height: 35px;
  width: 38px;
  text-align: center;
  left: 0px;
  padding-top: 6px;
}

.InformationButton {
  background-color: transparent;
  color: #326fff;
  border: none;
  font-size: 25px;
  padding: 0px;
  height: 25px;
}

.InformationButton:focus {
  -webkit-box-shadow: none;
  box-shadow: none;
}
</style>
